// Mirror of backend core/tips.py — same types, same priority, same rules —
// so trips generated before the tip gate existed still render one calm,
// prioritized voice on screen. New trips arrive with `tip_items` already
// gated by the backend; this only fills the gap for older documents.

export const TYPE_ORDER = ["BOOK", "TIMING", "MONEY", "ORDER", "DONT", "GETTING_IN", "AVOID", "INSIDER"];
export const LABELS = {
  BOOK: "Book ahead", TIMING: "Timing", MONEY: "Money", ORDER: "Order",
  DONT: "Don't", GETTING_IN: "Getting in", AVOID: "Avoid", INSIDER: "Insider",
};

const RULES = [
  ["ORDER", /^\s*order\s*:/i],
  ["BOOK", /\b(reserv|book(?!store)|ticket|sells? out|sold out|advance purchase|pre-?book|timed entry|waitlist)/i],
  ["MONEY", /(cash[- ]only|\bcash\b|¥|\byen\b|seating fee|cover charge|table charge|\bfee\b|coins?\b|ic card|suica|pasmo|no cards?)/i],
  ["DONT", /\b(don'?t|do not|never|avoid|no photos?|shoes off|remove (your )?shoes|(keep|stay|be) quiet|etiquette|rude|forbidden|not allowed|dip (once|only)|double[- ]dip|tipping|no tipping)\b/i],
  ["GETTING_IN", /\b(exit|entrance|enter (from|via|through)|gate\b|platform|side entrance|back entrance|north|south|east|west) /i],
  ["TIMING", /\b(arrive|before|after|by \d|\d{1,2}(:\d{2})?\s?(am|pm)|dawn|dusk|sunrise|sunset|early|late\b|closes?|closed|opens?|opening|last entry|weekday|weekend|monday|tuesday|wednesday|thursday|friday|saturday|sunday|queue|line up|crowd|peak|off-?peak|first thing|golden hour)\b/i],
];

export const classifyTip = (text) => {
  const t = (text || "").trim();
  for (const [type, rx] of RULES) if (rx.test(t)) return type;
  return "INSIDER";
};

// The one list every surface renders from. Prefers the backend's gated
// tip_items; falls back to classifying `tips` for older trips.
export const tipItems = (loc) => {
  if (!loc) return [];
  let items = Array.isArray(loc.tip_items) && loc.tip_items.length
    ? loc.tip_items.map(it => ({ type: it.type || classifyTip(it.text), text: (it.text || "").trim(), src: it.src || "catalog" }))
    : (loc.tips || []).filter(Boolean).map(t => ({ type: classifyTip(t), text: String(t).trim(), src: "catalog" }));
  items = items.filter(it => it.text);
  return items.sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type));
};


// The tip that matters AT THIS MOMENT, for the companion's NOW card.
// Food → what to order. Otherwise the highest-priority instruction; a
// closing window promotes TIMING; a DON'T leads when you're about to walk in.
export const contextualTip = (loc, { windowUrgent = false } = {}) => {
  const items = tipItems(loc);
  if (!items.length) return null;
  const isFood = (loc.category || "").toUpperCase() === "FOODIE" || items.some(i => i.type === "ORDER");
  const pick = (type) => items.find(i => i.type === type);
  if (windowUrgent && pick("TIMING")) return pick("TIMING");
  if (isFood && pick("ORDER")) return pick("ORDER");
  return pick("DONT") || pick("GETTING_IN") || pick("TIMING") || pick("MONEY") || pick("BOOK") || items[0];
};

// Group for the "read everything" layer: [{type, label, items}] in priority order.
export const groupedTips = (loc) => {
  const items = tipItems(loc);
  return TYPE_ORDER.map(type => ({ type, label: LABELS[type], items: items.filter(i => i.type === type) })).filter(g => g.items.length);
};

// Strip "Order:" prefix for display when the label already says Order.
export const tipText = (it) => (it?.type === "ORDER" ? it.text.replace(/^\s*order\s*:\s*/i, "") : it?.text || "");

const similar = (a, b) => {
  const la = (a || "").toLowerCase().trim(), lb = (b || "").toLowerCase().trim();
  return !!la && !!lb && (la === lb || la.includes(lb) || lb.includes(la));
};

// Everything that needs action before (or the night before) a day:
// bookings, cash, luggage forwarding. Optionally scoped to one day.
export const beforeYouGo = (itinerary, { dayNumber = null, includeLocked = false } = {}) => {
  const out = [];
  const days = [...(itinerary?.days || [])].sort((a, b) => (a.day_number || 0) - (b.day_number || 0));
  for (const d of days) {
    if (dayNumber != null && d.day_number !== dayNumber) continue;
    if (d.locked && !includeLocked) continue;
    for (const n of (d.editorial_notes || [])) {
      if (/luggage|forward|takkyubin|yamato/i.test(n) && !out.some(o => similar(o.text, n))) out.push({ day: d.day_number, place: null, type: "BOOK", text: n });
    }
    for (const b of (d.time_blocks || [])) for (const loc of (b.locations || [])) {
      for (const tip of (loc.preparation_tips || [])) {
        if (!out.some(o => similar(o.text, tip.text))) out.push({day:d.day_number, place:loc.name, type:tip.delivery_phases?.includes("before_booking") ? "BOOK" : "TIMING", text:tip.text});
      }
      for (const it of tipItems(loc)) {
      if ((it.type === "BOOK" && !loc.is_optional || it.type === "MONEY") && !out.some(o => similar(o.text, it.text))) out.push({ day: d.day_number, place: loc.name, type: it.type, text: it.text });
      }
    }
  }
  return out;
};

export const cashStops = (day) => (day?.time_blocks || []).flatMap(b => b.locations || [])
  .filter(l => l.cash_only === true || tipItems(l).some(i => i.type === "MONEY" && /cash/i.test(i.text)));
